import React, { useContext, useState } from 'react';

import { FieldArray, useFormikContext } from 'formik';

import ProjectActivityFormFooter from '../ProjectActivityFormFooter';

import ActivitySummaryStrip from './ActivitySummaryStrip';
import AddPredecessorRow from './AddPredecessorRow';
import { activityName } from './linkedActivity';
import PredecessorsTable from './PredecessorsTable';
import SuccessorsTable from './SuccessorsTable';

import FormSectionHeading from 'CommonComponents/FormSectionHeading';
import TextButton from 'CommonComponents/TextButton';

import ProjectPlanContext from 'Context/project_plan/ProjectPlanContext';

import PlusIcon from 'Assets/icons/plus.svg';

const LoopBanner = ({ loop }) => (
    <div role="alert" className="tw-rounded tw-border tw-border-red-25 tw-bg-red-10 tw-p-3 tw-text-sm tw-text-red">
        <div className="tw-font-medium">{$t('This link would create a loop of dependent activities.')}</div>
        <div>{loop.map(activityName).join(' → ')}</div>
    </div>
);

/**
 * Predecessors are staged in the form's `dependencies` array and saved with the activity.
 * Predecessors the user may not see and successors are shown as they are.
 */
const ActivityDependenciesTab = ({ mode, activityId, categoryId, maskedPredecessors, successors, onCancel }) => {
    const { canManage } = useContext(ProjectPlanContext);
    const { values, errors, status, isSubmitting } = useFormikContext();

    const [adding, setAdding] = useState(false);

    // Hidden without the right to manage; only disabled while saving, so a half-picked row survives.
    const readOnly = !canManage;
    const listError = typeof errors?.dependencies === 'string' ? errors.dependencies : null;
    const rows = values.dependencies || [];
    const excludeIds = [...rows.map((row) => row.predecessor_id), ...maskedPredecessors.map((link) => link.activity.id)];

    return (
        <FieldArray name="dependencies">
            {(arrayHelpers) => (
                <div className="tw-flex tw-flex-col tw-gap-y-6">
                    <ActivitySummaryStrip activityId={activityId} maskedPredecessors={maskedPredecessors} />
                    {!!status?.dependencyLoop?.length && <LoopBanner loop={status.dependencyLoop} />}

                    <div className="tw-flex tw-flex-col tw-gap-y-3">
                        <FormSectionHeading heading={$t('Predecessors')} />
                        {!!listError && <div className="tw-text-red-500 tw-text-sm">{listError}</div>}
                        <PredecessorsTable
                            rows={rows}
                            maskedPredecessors={maskedPredecessors}
                            readOnly={readOnly}
                            disabled={isSubmitting}
                            onRemove={arrayHelpers.remove}
                        />
                        {!readOnly &&
                            (adding ? (
                                <AddPredecessorRow
                                    mode={mode}
                                    activityId={activityId}
                                    categoryId={categoryId}
                                    excludeIds={excludeIds}
                                    disabled={isSubmitting}
                                    onAdd={(row) => {
                                        arrayHelpers.push(row);
                                        setAdding(false);
                                    }}
                                    onCancel={() => setAdding(false)}
                                />
                            ) : (
                                <div>
                                    <TextButton variant="primary" onClick={() => setAdding(true)} disabled={isSubmitting}>
                                        <PlusIcon className="tw-w-3 tw-h-3" />
                                        {$t('Add predecessor')}
                                    </TextButton>
                                </div>
                            ))}
                        <p className="tw-mb-0 tw-text-xs tw-text-charcoal-60">
                            {$t(
                                'Finish to start: this activity starts after the predecessor finishes. Start to start: this activity starts after the predecessor starts. Lag adds days after that point; use a negative number to overlap.',
                            )}
                        </p>
                    </div>

                    <div className="tw-flex tw-flex-col tw-gap-y-3">
                        <FormSectionHeading heading={$t('Successors')} />
                        <SuccessorsTable successors={successors} />
                    </div>

                    <ProjectActivityFormFooter onCancel={onCancel} />
                </div>
            )}
        </FieldArray>
    );
};

export default ActivityDependenciesTab;
